import { summarizePrintDeliveries } from "@/lib/print-deliveries"
import type {
  EditorialFunction,
  CatalogItemTypeField,
  CatalogItemType,
  CatalogCollectionSummary,
  CatalogProduct,
  KitPresetCatalogEntry,
  PipelineCatalogEntry,
  ProductionRun,
} from "@/types/domain"

const now = "2026-08-28T09:30:00.000Z"

export const demoCatalogCollections: CatalogCollectionSummary[] = [
  { id: "collection-classicos", name: "Clássicos", description: "Obras clássicas em edições comentadas.", active: true, version: 1, imprint: { id: "imprint-literatura", code: "LITERATURA", name: "Clube de Literatura" }, productCount: 1, createdAt: now, updatedAt: now },
  { id: "collection-ciencia-aberta", name: "Ciência Aberta", active: true, version: 1, imprint: { id: "imprint-ciencias", code: "CIENCIAS", name: "Clube de Ciências" }, productCount: 1, createdAt: now, updatedAt: now },
]

function physicalFields(prefix: string, includeThickness: boolean): CatalogItemTypeField[] {
  return [
    { id: `field-${prefix}-width`, code: "width_mm", label: "Largura", type: "DECIMAL", unit: "mm", options: [], position: 10, version: 1 },
    { id: `field-${prefix}-height`, code: "height_mm", label: "Altura", type: "DECIMAL", unit: "mm", options: [], position: 20, version: 1 },
    ...(includeThickness ? [{ id: `field-${prefix}-thickness`, code: "thickness_mm", label: prefix === "book" || prefix === "booklet" ? "Lombada" : "Espessura", type: "DECIMAL" as const, unit: "mm", options: [], position: 30, version: 1 }] : []),
  ]
}

const simplePhysicalTypes: CatalogItemType[] = [
  ["card", "CARD", "Card"], ["magnet", "MAGNET", "Ímã"], ["die-cut-seal", "DIE_CUT_SEAL", "Selo recortado"],
  ["postcard", "POSTCARD", "Postal"], ["envelope", "ENVELOPE", "Envelope"], ["wrap", "WRAP", "Embrulho"],
  ["sheet", "SHEET", "Folha"], ["poster", "POSTER", "Pôster"],
].map(([slug, code, name]) => ({
  id: `type-${slug}`, code, name, isBook: false, version: 1,
  fields: physicalFields(slug, true), variants: [],
}))

export const demoCatalogItemTypes: CatalogItemType[] = [
  {
    id: "type-book", code: "BOOK", name: "Livro", isBook: true, version: 1,
    fields: [
      { id: "field-book-paper", code: "paper", label: "Papel do miolo", type: "SELECT", unit: undefined, options: ["Pólen Soft 80 g/m²", "Offset 90 g/m²"], position: 1, version: 1 },
      { id: "field-book-colors", code: "colors", label: "Cores do miolo", type: "TEXT", unit: undefined, options: [], position: 2, version: 1 },
      ...physicalFields("book", true),
      { id: "field-book-binding", code: "binding", label: "Encadernação", type: "SELECT", options: ["Capa dura", "Brochura", "Grampo"], position: 40, version: 1 },
      { id: "field-book-weight", code: "weight_kg", label: "Peso", type: "DECIMAL", unit: "kg", options: [], position: 50, version: 1 },
    ],
    variants: [],
  },
  {
    id: "type-booklet", code: "BOOKLET", name: "Livreto", isBook: true, version: 1,
    fields: [
      { id: "field-booklet-paper", code: "paper", label: "Papel", type: "TEXT", options: [], position: 1, version: 1 },
      ...physicalFields("booklet", true),
      { id: "field-booklet-binding", code: "binding", label: "Encadernação", type: "SELECT", options: ["Capa dura", "Brochura", "Grampo"], position: 40, version: 1 },
      { id: "field-booklet-weight", code: "weight_kg", label: "Peso", type: "DECIMAL", unit: "kg", options: [], position: 50, version: 1 },
    ],
    variants: [],
  },
  {
    id: "type-bookmark", code: "BOOKMARK", name: "Marca-página", isBook: false, version: 1,
    fields: [
      { id: "field-bookmark-size", code: "size", label: "Formato", type: "TEXT", unit: "mm", options: [], position: 1, version: 1 },
      { id: "field-bookmark-paper", code: "paper", label: "Papel", type: "TEXT", options: [], position: 2, version: 1 },
      ...physicalFields("bookmark", true),
    ],
    variants: [],
  },
  {
    id: "type-packaging", code: "PACKAGING", name: "Caixa personalizada", isBook: false, version: 1,
    fields: [
      { id: "field-packaging-material", code: "material", label: "Material", type: "TEXT", options: [], position: 1, version: 1 },
      ...physicalFields("packaging", false),
      { id: "field-packaging-depth", code: "depth_mm", label: "Profundidade", type: "DECIMAL", unit: "mm", options: [], position: 30, version: 1 },
    ],
    variants: [],
  },
  ...simplePhysicalTypes,
]

const mockupSource = "book-mockup-studio/models.json"
const mockupHash = "4dfa64cd384a68386b29a52c1f2735a52210e396a9d42d8ed52e332ed4e6efcb"
const mockupModels = [
  ["PACKAGING", "caixa-personalizada", "Caixa personalizada", { width_mm: 300, height_mm: 60, depth_mm: 250 }, { kind: "box" }],
  ["BOOK", "livro-hard-cover-141x216", "Livro capa dura 141x216 mm", { width_mm: 141, height_mm: 216, binding: "Capa dura" }, { kind: "livro", mockupPreset: "hard-cover" }],
  ["BOOK", "livro-hard-cover-163x238", "Livro capa dura 163x238 mm", { width_mm: 163, height_mm: 238, binding: "Capa dura" }, { kind: "livro", mockupPreset: "hard-cover" }],
  ["BOOKLET", "livro-hard-cover-133x177", "Livreto capa dura 133x177 mm", { width_mm: 133, height_mm: 177, binding: "Capa dura" }, { kind: "livro", mockupPreset: "hard-cover" }],
  ["BOOKLET", "livro-booklet-135x210", "Livreto brochura 135x210 mm", { width_mm: 135, height_mm: 210, binding: "Brochura" }, { kind: "livro", mockupPreset: "booklet" }],
  ["BOOK", "livro-booklet-100x150", "Livro brochura 100x150 mm", { width_mm: 100, height_mm: 150, binding: "Brochura" }, { kind: "livro", mockupPreset: "booklet" }],
  ["BOOK", "livro-booklet-123x177", "Livro brochura 123x177 mm", { width_mm: 123, height_mm: 177, binding: "Brochura" }, { kind: "livro", mockupPreset: "booklet" }],
  ["BOOK", "livro-booklet-200x210", "Livro brochura 200x210 mm", { width_mm: 200, height_mm: 210, binding: "Brochura" }, { kind: "livro", mockupPreset: "booklet" }],
  ["BOOKLET", "livro-grampo-110x170", "Livreto grampo 110x170 mm", { width_mm: 110, height_mm: 170, thickness_mm: 4, binding: "Grampo" }, { kind: "livro", mockupPreset: "grampo" }],
  ["CARD", "card-200x200", "Card 200x200 mm", { width_mm: 200, height_mm: 200, thickness_mm: 0.3 }, { kind: "card" }],
  ["CARD", "card-400x200", "Card 400x200 mm", { width_mm: 400, height_mm: 200, thickness_mm: 0.3 }, { kind: "card" }],
  ["MAGNET", "ima-recorte-max-70x70", "Ímã recortado (máx. 70x70 mm)", { width_mm: 70, height_mm: 70, thickness_mm: 0.3 }, { kind: "card", renderMode: "alpha-cutout", backColor: "#3F3B38" }],
  ["DIE_CUT_SEAL", "selo-recorte-35x47", "Selo recortado 35x47 mm", { width_mm: 35, height_mm: 47, thickness_mm: 0.3 }, { kind: "card", renderMode: "alpha-cutout", backColor: "#F7F3EA" }],
  ["POSTCARD", "postal-80x120", "Postal 80x120 mm", { width_mm: 80, height_mm: 120, thickness_mm: 0.3 }, { kind: "card" }],
  ["POSTCARD", "postal-100x150", "Postal 100x150 mm", { width_mm: 100, height_mm: 150, thickness_mm: 0.3 }, { kind: "card" }],
  ["POSTCARD", "postal-150x100", "Postal 150x100 mm", { width_mm: 150, height_mm: 100, thickness_mm: 0.3 }, { kind: "card" }],
  ["ENVELOPE", "envelope-175x120", "Envelope 175x120 mm", { width_mm: 175, height_mm: 120, thickness_mm: 0.3 }, { kind: "card" }],
  ["BOOKMARK", "marca-pagina-50x200", "Marca-página 50x200 mm", { size: "50 × 200", width_mm: 50, height_mm: 200, thickness_mm: 0.3 }, { kind: "card" }],
  ["WRAP", "embrulho-430x590", "Embrulho 43x59 cm", { width_mm: 430, height_mm: 590, thickness_mm: 0.1 }, { kind: "sheet" }],
  ["SHEET", "folha-148x210", "Folha 148x210 mm", { width_mm: 148, height_mm: 210, thickness_mm: 0.1 }, { kind: "sheet" }],
  ["POSTER", "poster-a4-210x297", "Pôster A4 210x297 mm", { width_mm: 210, height_mm: 297, thickness_mm: 0.1 }, { kind: "sheet" }],
] as const

for (const [typeCode, externalId, name, values, metadata] of mockupModels) {
  const itemType = demoCatalogItemTypes.find((candidate) => candidate.code === typeCode)!
  itemType.variants.push({
    id: `variant-${externalId}`, externalSource: mockupSource, externalId, name, sourceHash: mockupHash,
    metadata, version: 1,
    fieldValues: Object.entries(values).map(([code, value]) => {
      const field = itemType.fields.find((candidate) => candidate.code === code)!
      return { fieldId: field.id, code, label: field.label, type: field.type, unit: field.unit, value }
    }),
  })
}

export const demoCatalogProducts: CatalogProduct[] = [
  {
    id: "catalog-estoicismo", sku: "BARCA-FIL-026", name: "Box Estoicismo", author: "Marco Aurélio", description: "Leitura principal, guia e materiais de apoio.",
    status: "IN_PRODUCTION", version: 4, imprint: { id: "imprint-filosofia", code: "FILOSOFIA", name: "Clube de Filosofia" },
    publicationMonth: 9, publicationYear: 2026, salesModel: "SUBSCRIPTION",
    currentRunId: "run-estoicismo-1", productionCount: 1, createdAt: "2026-05-10T12:00:00.000Z", updatedAt: now,
    items: [
      {
        id: "catalog-item-medita", productId: "catalog-estoicismo", name: "Meditações", position: 1, version: 3, currentEditionSequence: 1,
        itemType: { id: "type-book", code: "BOOK", name: "Livro", isBook: true },
        technicalValues: [
          { fieldId: "field-book-paper", code: "paper", label: "Papel do miolo", type: "SELECT", value: "Pólen Soft 80 g/m²" },
          { fieldId: "field-book-colors", code: "colors", label: "Cores do miolo", type: "TEXT", value: "1x1" },
        ],
        book: {
          subtitle: "Livro I", publisher: "Barca", publicationCity: "Belo Horizonte", editionLabel: "1ª edição", publicationYear: 2026,
          isbn: "9786580000005", pageCount: 224, format: "Brochura", dimensions: "14 × 21 cm", binding: "PUR", language: "Português",
          technicalExtras: [],
          credits: [{ id: "credit-marco", contributor: { id: "contributor-marco", name: "Marco Aurélio" }, roleCode: "AUTHOR", roleName: "Autor", displayLabel: "Autor", position: 1 }],
          catalogRecord: { transcription: "Marco Aurélio. Meditações.", cataloger: "Equipe Barca", cdd: "188", cdu: "1", subjects: ["Estoicismo"], notes: "", extraFields: [], version: 1 },
          fao: { synopsis: "Reflexões sobre virtude, dever e serenidade.", context: "Roma antiga.", mainThemes: "Ética e estoicismo.", curiosities: "", authorBiography: "Imperador romano e filósofo estoico.", mainWorks: "", editionHighlights: "", referenceLinks: "", version: 1 },
        },
        documents: [],
      },
      {
        id: "catalog-item-guia", productId: "catalog-estoicismo", name: "Guia de leitura", position: 2, version: 1, currentEditionSequence: 1,
        itemType: { id: "type-booklet", code: "BOOKLET", name: "Livreto", isBook: true },
        technicalValues: [{ fieldId: "field-booklet-paper", code: "paper", label: "Papel", type: "TEXT", value: "Offset 90 g/m²" }],
        book: { pageCount: 32, format: "Grampo", dimensions: "12 × 18 cm", technicalExtras: [], credits: [] }, documents: [],
      },
      {
        id: "catalog-item-marcador", productId: "catalog-estoicismo", name: "Marca-página Círculo do controle", position: 3, version: 1, currentEditionSequence: 1,
        itemType: { id: "type-bookmark", code: "BOOKMARK", name: "Marca-página", isBook: false },
        technicalValues: [
          { fieldId: "field-bookmark-size", code: "size", label: "Formato", type: "TEXT", unit: "mm", value: "50 × 180" },
          { fieldId: "field-bookmark-paper", code: "paper", label: "Papel", type: "TEXT", value: "Couché 300 g/m²" },
        ], documents: [],
      },
    ],
  },
  {
    id: "catalog-metamorfose", sku: "BARCA-LIT-014", name: "Box A metamorfose", author: "Franz Kafka", description: "Edição comentada com caderno de apoio.",
    status: "READY", version: 2, imprint: { id: "imprint-literatura", code: "LITERATURA", name: "Clube de Literatura" },
    collection: { id: "collection-classicos", name: "Clássicos", active: true },
    publicationMonth: 11, publicationYear: 2025, salesModel: "SUBSCRIPTION",
    productionCount: 2, createdAt: "2025-11-05T12:00:00.000Z", updatedAt: "2026-07-15T14:00:00.000Z",
    items: [{
      id: "catalog-item-kafka", productId: "catalog-metamorfose", name: "A metamorfose", position: 1, version: 1, currentEditionSequence: 1,
      itemType: { id: "type-book", code: "BOOK", name: "Livro", isBook: true },
      technicalValues: [
        { fieldId: "field-book-paper", code: "paper", label: "Papel do miolo", type: "SELECT", value: "Pólen Soft 80 g/m²" },
        { fieldId: "field-book-colors", code: "colors", label: "Cores do miolo", type: "TEXT", value: "1x1" },
      ],
      book: { publicationYear: 2025, isbn: "9786580000012", pageCount: 144, format: "Brochura", dimensions: "14 × 21 cm", editorialFilesUrl: "https://drive.google.com/drive/folders/metamorfose", technicalExtras: [], credits: [{ id: "credit-kafka", contributor: { id: "contributor-kafka", name: "Franz Kafka" }, roleCode: "AUTHOR", roleName: "Autor", displayLabel: "Autor", position: 1 }] }, documents: [],
    }],
  },
  {
    id: "catalog-cosmos", sku: "BARCA-CIE-008", name: "Kit Cosmos", description: "Produto em preparação para ativação.",
    status: "DRAFT", version: 1, imprint: { id: "imprint-ciencias", code: "CIENCIAS", name: "Clube de Ciências" },
    collection: { id: "collection-ciencia-aberta", name: "Ciência Aberta", active: true },
    publicationMonth: 8, publicationYear: 2026, salesModel: "STANDALONE",
    productionCount: 0, createdAt: now, updatedAt: now, items: [],
  },
]

const productionStages = [
  { id: "stage-briefing", name: "Briefing", shortName: "Briefing", position: 1 },
  { id: "stage-editorial", name: "Editorial", shortName: "Editorial", position: 2 },
  { id: "stage-design", name: "Design", shortName: "Design", position: 3 },
]
const productionStageFunctions: Record<string, EditorialFunction> = { "stage-briefing": "EDITOR_IN_CHIEF", "stage-editorial": "TEXT_REVIEWER", "stage-design": "DESIGNER" }

/** Item da demonstração com a tiragem entregue em duas partes pela gráfica. */
function demoPartialPrintDeliveries() {
  const printDeliveries = [
    { id: "demo-entrega-1", estimatedDate: "2026-10-15", estimatedQuantity: 1000, deliveryDate: "2026-10-17", receivedQuantity: 1000 },
    { id: "demo-entrega-2", estimatedDate: "2026-11-12", estimatedQuantity: 1500, deliveryDate: null, receivedQuantity: null },
  ]
  return { printDeliveries, ...summarizePrintDeliveries(printDeliveries) }
}

export const demoProductionRuns: ProductionRun[] = [{
  id: "run-estoicismo-1", sequence: 1, referenceMonth: "2026-09", kitQuantity: 2500, status: "ACTIVE", version: 2,
  sourceCatalogStatus: "DRAFT",
  startedAt: "2026-08-19T12:00:00.000Z",
  product: { id: "catalog-estoicismo", sku: "BARCA-FIL-026", name: "Box Estoicismo", imprint: "Clube de Filosofia", description: "Leitura principal, guia e materiais de apoio.", status: "IN_PRODUCTION" },
  pipeline: { id: "pipeline-filosofia-v1", templateId: "pipeline-filosofia", name: "Publicação mensal", version: 1 }, stages: productionStages,
  items: demoCatalogProducts[0].items.map((item, index) => ({
    id: `production-${item.id}`, runId: "run-estoicismo-1", catalogItemId: item.id, name: item.name, position: item.position,
    plannedQuantity: 2500, status: index === 2 ? "BLOCKED" : "ACTIVE", version: 1, itemType: item.itemType,
    printShop: index === 0 ? { id: "print-shop-atlas", name: "Gráfica Atlas", address: "", contact: "" } : undefined,
    ...(index === 1 ? demoPartialPrintDeliveries() : {}),
    technicalValues: structuredClone(item.technicalValues),
    stageStates: productionStages.map((stage, stageIndex) => ({
      id: `state-${item.id}-${stage.id}`, stageId: stage.id, stageName: stage.name, stageShortName: stage.shortName,
      stagePosition: stage.position,
      status: stageIndex === 0 ? (index === 2 ? "BLOCKED" : "IN_PROGRESS") : "NOT_STARTED", version: 1,
      enteredAt: stageIndex === 0 ? "2026-08-19T12:00:00.000Z" : undefined,
      blockedAt: stageIndex === 0 && index === 2 ? "2026-08-27T15:00:00.000Z" : undefined,
      blockedReason: stageIndex === 0 && index === 2 ? "Formato final da faca pendente." : undefined,
      checklists: stageIndex === 0 ? [{ id: `check-${item.id}`, label: "Briefing validado pelo responsável", required: false, status: index === 0 ? "DONE" : "PENDING", version: 1 }] : [],
    })),
  })),
  gates: [{ id: "gate-run-kit", label: "Todos os componentes conferidos como um box", required: false, status: "PENDING", version: 1 }],
  followers: [{ id: "demo-ops", name: "Caio Nunes" }, { id: "demo-admin", name: "Marina Rocha" }],
  lead: { id: "demo-admin", name: "Marina Rocha" },
  assignments: [
    { stageId: "stage-briefing", user: { id: "demo-admin", name: "Marina Rocha" }, assignedAt: "2026-08-19T12:00:00.000Z" },
    { stageId: "stage-editorial", user: { id: "demo-ops", name: "Caio Nunes" }, assignedAt: "2026-08-19T12:00:00.000Z" },
    { stageId: "stage-design", user: { id: "demo-admin", name: "Marina Rocha" }, assignedAt: "2026-08-19T12:00:00.000Z" },
  ],
  changes: [{ id: "change-1", type: "CATALOG_UPDATED", reason: "Correção da especificação do papel", before: { paper: "Offset 75 g/m²" }, after: { paper: "Pólen Soft 80 g/m²" }, actor: "Marina Rocha", createdAt: now }],
}]

export const demoKitPresets: KitPresetCatalogEntry[] = structuredClone(demoTemplateCatalogLikeKitPresets())
export const demoPipelineCatalog: PipelineCatalogEntry[] = structuredClone(demoTemplateCatalogLikePipelines())

function demoTemplateCatalogLikeKitPresets(): KitPresetCatalogEntry[] {
  return [{ id: "kit-filosofia", name: "Box mensal padrão", imprintId: "imprint-filosofia", imprintName: "Clube de Filosofia", items: [
    { id: "preset-book", itemTypeId: "type-book", itemTypeCode: "BOOK", itemTypeName: "Livro", variantId: "variant-livro-hard-cover-141x216", variantName: "Livro capa dura 141x216 mm", defaultName: "Livro principal", position: 1 },
    { id: "preset-booklet", itemTypeId: "type-booklet", itemTypeCode: "BOOKLET", itemTypeName: "Livreto", variantId: "variant-livro-booklet-135x210", variantName: "Livreto brochura 135x210 mm", defaultName: "Guia de leitura", position: 2 },
    { id: "preset-bookmark", itemTypeId: "type-bookmark", itemTypeCode: "BOOKMARK", itemTypeName: "Marca-página", variantId: "variant-marca-pagina-50x200", variantName: "Marca-página 50x200 mm", defaultName: "Marca-página", position: 3 },
  ] }]
}

function demoTemplateCatalogLikePipelines(): PipelineCatalogEntry[] {
  return [{ id: "pipeline-filosofia", name: "Publicação mensal", active: true, hasProductionRuns: true, canDelete: false, versions: [{ id: "pipeline-filosofia-v1", version: 1, publishedAt: now, createdAt: now, stages: productionStages.map((stage) => ({ ...stage, editorialFunction: productionStageFunctions[stage.id], applicableItemTypeIds: demoCatalogItemTypes.map((type) => type.id), checklists: [] })), gates: [{ id: "gate-kit", label: "Conferência final do box", required: false, position: 1 }] }] }]
}
